/**
 * React Query hooks for Onboarding Status API (R3-1)
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getApiClient } from "../lib/api";
import type {
  CreateOnboardingWorkspaceRequest,
  OnboardingPreset,
} from "../types/api";

const ONBOARDING_KEYS = {
  status: (workspaceId: string) => ["onboarding", "status", workspaceId] as const,
};

/**
 * ワークスペースのオンボーディング進捗ステータスを取得するフック
 *
 * 接続済みアダプター数・蓄積ドキュメント数・heartbeat 状態・24h カウントダウンを返す。
 */
export function useOnboardingStatus(workspaceId: string) {
  return useQuery({
    queryKey: ONBOARDING_KEYS.status(workspaceId),
    queryFn: () => getApiClient().getWorkspaceStatus(workspaceId),
    staleTime: 1000 * 30, // 30秒ごとに再フェッチ
    enabled: workspaceId.length > 0,
  });
}

/**
 * 「今すぐ開始」ボタン用 mutation — POST /api/adapters/heartbeat/start
 *
 * 成功後にオンボーディングステータスを再フェッチする。
 */
export function useForceStartHeartbeat(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => getApiClient().startHeartbeat(workspaceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ONBOARDING_KEYS.status(workspaceId) });
    },
  });
}

export function useCreateOnboardingWorkspace() {
  return useMutation({
    mutationFn: (input: CreateOnboardingWorkspaceRequest) =>
      getApiClient().createOnboardingWorkspace(input),
  });
}

export function useStartOnboardingAutoSetup(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (preset: OnboardingPreset) =>
      getApiClient().startOnboardingAutoSetup(workspaceId, preset),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ONBOARDING_KEYS.status(workspaceId) });
    },
  });
}
